/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { memo, useMemo } from 'react'
import tw from 'twin.macro'
import { MCDetails } from 'src/interfaces/mc'
import { MCStatus } from 'src/constants/mc/enums'
import { MCPendingStep } from '../shared/MCPendingStep'
import {
  CustomerCollectedDataStatus,
  CustomerCollectedDataType,
  CustomerMCLinkInfoType,
  CustomerResubmitInfoType,
} from 'src/constants/customer/enums'
import { MCStepItem, MCStepParams } from '../shared/MCStepItem'
import { MCStepDetails } from '../types'
import { getMCBusinessType } from 'src/utils/mcUtils'
import { StitchVendorStates } from 'src/constants/payment/enums'

interface Props {
  mcDetails: MCDetails
  onSelectStep: (stepDetails: MCStepDetails) => void
}

const OnboardingStepsTabComponent = memo(({ mcDetails, onSelectStep }: Props) => {
  const steps: MCStepParams[] = useMemo(() => {
    const businessInfoStatus = !!mcDetails.partner.partnerInformation
      ? CustomerCollectedDataStatus.Completed
      : CustomerCollectedDataStatus.Created

    const res: MCStepParams[] = [
      {
        stepStatus: businessInfoStatus,
        stepType: CustomerResubmitInfoType.BusinessInfo,
        onViewDetails: !!mcDetails.partner.partnerInformation
          ? () => {
              const data = mcDetails.partner.partnerInformation!

              const { businessType, otherBusinessType } = getMCBusinessType(data.businessType)

              onSelectStep({
                stepData: [
                  {
                    title: 'Business type',
                    text: businessType,
                  },
                  ...(otherBusinessType
                    ? [
                        {
                          title: 'Other business type',
                          text: otherBusinessType,
                        },
                      ]
                    : []),
                  {
                    title: 'DOT number',
                    text: data.dotNumber,
                  },
                  {
                    title: 'Address - 1',
                    text: data.addressStreet1,
                  },
                  {
                    title: 'Address - 2',
                    text: data.addressStreet2,
                  },
                  {
                    title: 'State',
                    text: data.addressState,
                  },
                  {
                    title: 'Zip code',
                    text: data.addressPostalCode,
                  },
                ],
                stepStatus: businessInfoStatus,
                stepType: CustomerResubmitInfoType.BusinessInfo,
                documents: [],
              })
            }
          : undefined,
      },
    ]

    mcDetails.partner.partnerCollectedData.forEach((data) => {
      if (data.type === CustomerCollectedDataType.TSA) {
        const stepData = [
          {
            title:
              'Registered Agent and a Certified IAC (Indirect Air Carrier) established in VeRoot with TSA certified drivers',
            text: data.data?.isRegisteredAgent ? 'YES' : 'NO',
          },
        ]

        if (data.data?.isRegisteredAgent) {
          stepData.push(
            {
              title: 'IAC Number',
              text: data.data?.iacNumber || '-',
            },
            {
              title: 'Date of Last Certification',
              text: data.data?.dateOfLastCertification || '-',
            },
            {
              title: 'Agent Name',
              text: data.data?.agentName || '-',
            },
            {
              title: 'Coordinator Name',
              text: data.data?.coordinatorName || '-',
            },
            {
              title: 'Coordinator Phone Number',
              text: data.data?.coordinatorPhoneNumber || '-',
            },
            {
              title: 'Coordinator Email',
              text: data.data?.coordinatorEmail || '-',
            },
          )
        } else {
          stepData.push(
            {
              title: 'Has TWIC',
              text: data.data?.hasTwic ? 'YES' : 'NO',
            },
            {
              title: 'TWIC Contractors Number',
              text: `${data.data?.twicContractorsNumber}` || '-',
            },
            {
              title: 'Has FAST',
              text: data.data?.hasFast ? 'YES' : 'NO',
            },
            {
              title: 'FAST Contractors Number',
              text: `${data.data?.fastContractorsNumber}` || '-',
            },
            {
              title: 'Has SIDA',
              text: data.data?.hasSida ? 'YES' : 'NO',
            },
            {
              title: 'SIDA Contractors Number',
              text: `${data.data?.sidaContractorsNumber}` || '-',
            },
            {
              title: 'Has Hazmat',
              text: data.data?.hasHazmat ? 'YES' : 'NO',
            },
            {
              title: 'Hazmat Contractors Number',
              text: `${data.data?.hazmatContractorsNumber}` || '-',
            },
          )
        }

        res.push({
          stepStatus: data.status,
          stepType: data.type,
          expiresAt: data.expiresAt || undefined,
          onViewDetails:
            data.status === CustomerCollectedDataStatus.Completed ||
            data.status === CustomerCollectedDataStatus.Failed
              ? () => {
                  onSelectStep({
                    stepData: stepData,
                    stepStatus: data.status,
                    stepType: CustomerCollectedDataType.TSA,
                    documents: data.documents,
                    expiresAt: data.expiresAt || undefined,
                  })
                }
              : undefined,
        })
      }
    })

    mcDetails.partner.partnerCollectedData.forEach((data) => {
      if (data.type === CustomerCollectedDataType.Insurance) {
        res.push({
          stepStatus: data.status,
          stepType: data.type,
          expiresAt: data.expiresAt || undefined,
          onViewDetails: data.documents.length
            ? () => {
                onSelectStep({
                  stepData: [],
                  stepStatus: data.status,
                  stepType: CustomerCollectedDataType.Insurance,
                  documents: data.documents,
                  expiresAt: data.expiresAt || undefined,
                })
              }
            : undefined,
        })
      }
    })

    mcDetails.partner.partnerCollectedData.forEach((data) => {
      if (data.type === CustomerCollectedDataType.BusinessDetails) {
        res.push({
          stepStatus: data.status,
          stepType: data.type,
          expiresAt: data.expiresAt || undefined,
          onViewDetails:
            data.status === CustomerCollectedDataStatus.Completed ||
            data.status === CustomerCollectedDataStatus.Failed
              ? () => {
                  onSelectStep({
                    stepData: [
                      {
                        title: 'Does Business Advertise',
                        text: data.data?.doesBusinessAdvertise ? 'YES' : 'NO',
                      },
                      {
                        title: 'Has Quality Program',
                        text: data.data?.hasQualityProgram ? 'YES' : 'NO',
                      },
                      {
                        title: 'Covered Cities Details',
                        text: data.data?.coveredCitiesDetails || '-',
                      },
                      {
                        title: 'Vehicle Access Details',
                        text: data.data?.vehicleAccessDetails || '-',
                      },
                      {
                        title: 'Quality Program Details',
                        text: data.data?.qualityProgramDetails || '-',
                      },
                    ],
                    stepStatus: data.status,
                    stepType: CustomerCollectedDataType.BusinessDetails,
                    documents: [],
                    expiresAt: data.expiresAt || undefined,
                  })
                }
              : undefined,
        })
      }
    })

    let paymentMethodStatus = CustomerCollectedDataStatus.Created

    if (mcDetails.partner.paymentInformation.stitchVendorState === StitchVendorStates.Pending) {
      paymentMethodStatus = CustomerCollectedDataStatus.InProgress
    }

    if (mcDetails.partner.paymentInformation.stitchVendorPaymentMethodActive) {
      paymentMethodStatus = CustomerCollectedDataStatus.Completed
    }

    res.push({
      stepStatus: paymentMethodStatus,
      stepType: CustomerResubmitInfoType.PaymentInfo,
    })

    mcDetails.partner.partnerCollectedData.forEach((data) => {
      if (data.type === CustomerCollectedDataType.GigsafeContract) {
        res.push({
          stepStatus: data.status,
          stepType: data.type,
          expiresAt: data.expiresAt || undefined,
          onViewDetails:
            data.status === CustomerCollectedDataStatus.Completed ||
            data.status === CustomerCollectedDataStatus.Failed
              ? () => {
                  onSelectStep({
                    stepData: [],
                    stepStatus: data.status,
                    stepType: CustomerCollectedDataType.GigsafeContract,
                    documents: data.documents,
                    expiresAt: data.expiresAt || undefined,
                  })
                }
              : undefined,
        })
      }
    })

    const signContractCollectedData = mcDetails.masterContractorCollectedData[0]

    const signContractStatus =
      signContractCollectedData &&
      signContractCollectedData.status !== CustomerCollectedDataStatus.Created
        ? CustomerCollectedDataStatus.Completed
        : CustomerCollectedDataStatus.Created

    const documents = mcDetails.masterContractorCollectedData.map((item) => item.documents).flat()

    res.push({
      stepStatus: signContractStatus,
      stepType: CustomerMCLinkInfoType.SignContract,
      onViewDetails: !!documents.length
        ? () => {
            onSelectStep({
              stepData: [],
              stepStatus: signContractStatus,
              stepType: CustomerMCLinkInfoType.SignContract,
              documents,
            })
          }
        : undefined,
    })

    return res
  }, [mcDetails])

  return (
    <div css={tw`flex flex-1 flex-col overflow-y-scroll h-full bg-[rgba(246, 246, 246, 1)]`}>
      <div css={tw`p-4 overflow-y-scroll pb-20`}>
        {mcDetails.status === MCStatus.Invited && (
          <div css={tw`flex flex-col gap-2`}>
            <MCPendingStep mcDetails={mcDetails} />
            {steps.map(
              ({
                stepStatus,
                stepType,
                expiresAt,
                onResubmitInfo,
                onChangeExpirationDate,
                onViewDetails,
              }) => (
                <MCStepItem
                  stepStatus={stepStatus}
                  key={stepType}
                  stepType={stepType}
                  expiresAt={expiresAt}
                  onResubmitInfo={onResubmitInfo}
                  onViewDetails={onViewDetails}
                  onChangeExpirationDate={onChangeExpirationDate}
                />
              ),
            )}
          </div>
        )}

        {(mcDetails.status === MCStatus.Activated || mcDetails.status === MCStatus.Deactivated) && (
          <div css={tw`flex flex-col gap-2`}>
            {steps.map(
              ({
                stepStatus,
                stepType,
                expiresAt,
                onResubmitInfo,
                onChangeExpirationDate,
                onViewDetails,
              }) => (
                <MCStepItem
                  stepStatus={stepStatus}
                  key={stepType}
                  stepType={stepType}
                  expiresAt={expiresAt}
                  onResubmitInfo={onResubmitInfo}
                  onViewDetails={onViewDetails}
                  onChangeExpirationDate={onChangeExpirationDate}
                />
              ),
            )}
          </div>
        )}
      </div>
    </div>
  )
})

export const OnboardingStepsTab = OnboardingStepsTabComponent
